<template>
  <a-drawer
    :open="visible"
    :title="`编辑服务器 · ${node?.name || ''}`"
    width="480"
    @close="$emit('update:visible', false)"
  >
    <a-form v-if="node" layout="vertical" :model="form">
      <a-form-item label="服务器名">
        <a-input :value="node.name" disabled />
        <template #extra>服务器名创建后不能改（Agent 用作标识）</template>
      </a-form-item>
      <a-form-item
        label="远程地址（IP / 域名）"
        extra="可填写公网 IP 或二级域名；复制节点配置时实时解析域名。"
      >
        <a-input v-model:value="form.ipPublic" placeholder="如 1.2.3.4 或 hbt1.wch1.top" />
      </a-form-item>
      <a-form-item
        label="到期时间"
        extra="过期后该服务器不能创建新节点；已有节点不受影响。留空=永不过期。设为过去时间可立即禁用该服务器。"
      >
        <a-input
          type="datetime-local"
          v-model:value="form.expireAtStr"
          style="width:100%"
        />
        <div style="margin-top:4px">
          <a-button
            v-if="form.expireAtStr"
            size="small"
            type="link"
            style="padding:0"
            @click="form.expireAtStr = ''"
          >清除（设为永不过期）</a-button>
        </div>
      </a-form-item>
      <a-form-item label="总成本 (元)">
        <a-input-number
          v-model:value="form.totalCostYuan"
          :min="0"
          :precision="2"
          placeholder="留空 = 未设置"
          style="width:100%"
        />
      </a-form-item>
      <a-form-item
        label="下行带宽 (Mbps)"
        extra="1 Mbps 约等于 128 KB/s；留空表示不判断跑满。"
      >
        <a-input-number
          v-model:value="form.bandwidthDownMbps"
          :min="0.01"
          :precision="2"
          placeholder="如 1"
          style="width:100%"
        />
      </a-form-item>
      <a-form-item
        label="服务器预置端口"
        extra="创建节点时优先按顺序使用未占用端口；清空后恢复普通分配。"
      >
        <a-select
          v-model:value="form.presetPorts"
          mode="tags"
          :token-separators="[',', '，', ' ']"
          placeholder="例如 10010, 10009, 44066"
        />
      </a-form-item>
      <a-button
        type="primary"
        :loading="loading"
        block
        @click="onSubmit"
      >保存</a-button>
    </a-form>
  </a-drawer>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { updateNode } from '../api/nodes';
import type { NodeItem } from '../types';

const props = defineProps<{ visible: boolean; node: NodeItem | null }>();
const emit = defineEmits<{ (e: 'update:visible', v: boolean): void; (e: 'saved'): void }>();

// We use a plain string ("YYYY-MM-DDTHH:MM:SS" in browser local
// time) for the date input rather than a Date object. String round-trips
// cleanly through the native <input type="datetime-local"> and avoids
// the Day.js / Date / timezone confusion that bit us with
// a-date-picker. The browser is responsible for showing the picker
// UI in the user's locale.
interface FormState {
  ipPublic: string;
  expireAtStr: string; // "" = never expires; otherwise "YYYY-MM-DDTHH:MM:SS" local
  totalCostYuan: number | null; // null = 未设置 (DB NULL); 0 = explicit free
  bandwidthDownMbps: number | null; // null = 不判断跑满
  presetPorts: string[];
}
const form = reactive<FormState>({
  ipPublic: '',
  expireAtStr: '',
  totalCostYuan: null,
  bandwidthDownMbps: null,
  presetPorts: [],
});
const loading = ref(false);

// Convert an API-returned timestamp to the local-naive
// "YYYY-MM-DDTHH:MM:SS" string that <input type="datetime-local">
// expects. The API returns two formats depending on driver path:
//   - ISO with offset:  "2026-12-31T23:59:59+08:00"
//   - MySQL DATETIME:   "2026-07-24 16:09:00"  (admin server local time)
// We must handle BOTH. The MySQL form has no 'T' separator and no
// timezone marker; treat it as already-local (since the admin binary
// runs in the server's local TZ — same one the operator sees).
const isoToLocal = (s: string): string => {
  if (!s) return '';
  // ISO 8601 with T separator (Go's time.RFC3339Nano or DB Scan with
  // location). Use the offset baked into the string so the displayed
  // time matches the actual instant stored.
  let m = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::\d{2})?(.*)$/);
  if (m) {
    const date = m[1];
    const hm = m[2];
    const offset = m[3]; // "", "Z", "+08:00", etc.
    if (offset === '' || offset === 'Z') {
      // Naive or UTC — convert UTC to local.
      const d = new Date(`${date}T${hm}:00${offset === 'Z' ? 'Z' : ''}`);
      if (isNaN(d.getTime())) return '';
      return toLocalNaive(d);
    }
    // Has offset (e.g. +08:00) — use the offset to render the local
    // wall clock of THAT timezone (which is what the picker wants).
    return `${date}T${hm}:00`;
  }
  // MySQL DATETIME form: "2026-07-24 16:09:00" — no timezone, treat
  // as local wall clock.
  m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)$/);
  if (m) {
    return `${m[1]}T${m[2].slice(0, 5)}:00`;
  }
  return '';
};

// Format a Date as "YYYY-MM-DDTHH:MM:SS" using the browser's local
// timezone (so the picker shows what the user will perceive).
const toLocalNaive = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

watch(
  () => props.visible,
  (v) => {
    if (v && props.node) {
      form.ipPublic = props.node.ip_public || '';
      form.expireAtStr = isoToLocal(props.node.expire_at || '');
      // total_cost_cents is in cents; convert to yuan for the input.
      // null in DB → leave field empty (no "0" placeholder).
      form.totalCostYuan = props.node.total_cost_cents == null
        ? null
        : props.node.total_cost_cents / 100;
      form.bandwidthDownMbps = props.node.bandwidth_down_mbps == null
        ? null
        : Number(props.node.bandwidth_down_mbps);
      form.presetPorts = (props.node.preset_ports || []).map(String);
    }
  },
);

// "2026-07-02T23:59:59" (datetime-local naive) → ISO with local offset,
// which is what the backend parses via time.Parse(time.RFC3339, ...).
// We use the browser's local offset string so the stored UTC instant
// matches what the user picked.
const localToISO = (s: string): string | null => {
  if (!s) return null;
  // datetime-local always returns naive form. We rebuild with the
  // browser's offset so the backend gets a valid RFC3339 string.
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

const onSubmit = async () => {
  if (!props.node) {
    message.error('服务器信息缺失，请关闭重试');
    return;
  }
  const presetPorts = form.presetPorts.map((value) => String(value).trim()).filter(Boolean);
  if (presetPorts.some((value) => !/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 65535)) {
    message.error('预置端口必须是 1-65535 的整数');
    return;
  }
  loading.value = true;
  try {
    const body: any = {
      ip_public: (form.ipPublic || '').trim(),
      expire_at: localToISO(form.expireAtStr),
      // total_cost_cents is in cents; null = leave DB NULL.
      // Empty / cleared input → send null.
      total_cost_cents:
        form.totalCostYuan == null ? null : Math.round(form.totalCostYuan * 100),
      bandwidth_down_mbps:
        form.bandwidthDownMbps == null ? null : form.bandwidthDownMbps,
      preset_ports: [...new Set(presetPorts.map(Number))],
    };
    await updateNode(props.node.id, body);
    message.success('已保存');
    emit('saved');
    emit('update:visible', false);
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '保存失败');
  } finally {
    loading.value = false;
  }
};
</script>
